/**
 * Two-step add-to-cart for bespoke products (custom grilles, gallery rails, etc).
 *
 * Step 1: POST /apps/product-creator — given a SKU + metadata, creates a Shopify
 *         draft product/variant on the fly and returns its variantId.
 * Step 2: POST /cart/add.js — adds that variant with caller-supplied line item
 *         properties (the human-readable spec) and fires the standard cart events.
 *
 * @param {object} options
 * @param {object} options.creatorPayload - JSON body for /apps/product-creator.
 *   Must include at minimum: { sku, title, currency }.
 *   Include shopify_id / company_location_id only when non-zero.
 * @param {object} options.properties - Key/value pairs written as line item
 *   properties on the cart item (e.g. { "Rail size": "small", "Finish": "SBL" }).
 * @param {number|string} [options.quantity=1] - How many to add. Coerced to a
 *   whole number of at least 1, so a blank or nonsense quantity input adds one
 *   rather than failing the request.
 * @returns {Promise<object>} The cart item returned by /cart/add.js.
 * @throws {Error} If either HTTP request fails.
 */

async function addBespokeProductToCart({
  creatorPayload,
  properties,
  quantity = 1,
}) {
  const creatorRes = await fetch("/apps/product-creator", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(creatorPayload),
  });

  if (!creatorRes.ok) {
    const err = await creatorRes.json().catch(() => ({}));
    throw new Error(err.error || "Failed to create product");
  }

  const { product } = await creatorRes.json();

  const requested = Math.trunc(Number(quantity));
  const formData = new FormData();
  formData.append("id", product.variantId);
  formData.append(
    "quantity",
    Number.isFinite(requested) && requested > 0 ? requested : 1,
  );
  Object.entries(properties).forEach(([key, value]) => {
    formData.append(`properties[${key}]`, value);
  });

  const cartRes = await fetch("/cart/add.js", {
    method: "POST",
    body: formData,
  });
  const cartData = await cartRes.json();

  if (!cartRes.ok) {
    throw new Error(
      cartData.description || cartData.message || "Failed to add to cart",
    );
  }

  AM.updateHeaderCartItems();
  window.dispatchEvent(new Event("cart-updated"));

  return cartData;
}
